<!-- src/views/ForecastView.vue -->
<template>
  <div class="container forecast-view">
    <h4>Forecast</h4>

    <div v-if="error" class="card-panel red lighten-5 red-text text-darken-4">
      {{ error }}
    </div>

    <div
      v-if="loading"
      class="forecast-loading"
      role="status"
      aria-label="Loading forecast"
    >
      <div class="progress forecast-progress">
        <div class="determinate forecast-progress-fill"></div>
      </div>
    </div>

    <div class="forecast-week-list">
      <ForecastWeekInput
        v-for="wk in weeks"
        :key="`${venueId}-${wk.weekEndingDateISO}`"
        :week="wk"
        :categories="CATEGORIES"
        :days="DAYS"
        :formatUK="formatUK"
        :editable="isEditable(wk.weekEndingDateISO)"
        :submitted="isSubmitted(wk.weekEndingDateISO)"
        :canSubmit="canSubmit(wk.weekEndingDateISO)"
        :canAdjust="canAdjust(wk.weekEndingDateISO)"
        :busy="loading"
        :lockMessage="lockMessageForWeek(wk.weekEndingDateISO)"
        @cell="
          ({ category, dayIndex, value }) =>
            setCell(wk.weekEndingDateISO, category, dayIndex, value)
        "
        @submit="() => submitWeek(wk.weekEndingDateISO)"
        @adjust="() => enableAdjust(wk.weekEndingDateISO)"
        @stop-adjust="() => disableAdjust(wk.weekEndingDateISO)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "@/firebase";

import ForecastWeekInput from "@/components/ForecastWeekInput.vue";
import { useForecasts } from "@/scripts/useScripts/useForecasts";
import { useUserRole } from "@/scripts/useScripts/useUserRole";

// ==========================
// Venue
// ==========================
function cleanVenueId(value) {
  return String(value || "").trim();
}

function getStoredVenueId() {
  return cleanVenueId(localStorage.getItem("venue"));
}

const venueId = ref(getStoredVenueId());

// ==========================
// Auth user
// ==========================
const userRef = ref({ uid: null, email: null });

// ==========================
// Role / Admin
// ==========================
const { isAdmin, roleLoaded, loadUserRole } = useUserRole();
const isAdminRef = isAdmin;

// ==========================
// Forecast logic
// ==========================
const {
  CATEGORIES,
  DAYS,
  loading,
  error,
  weeks,
  formatUK,

  isInCurrentPlus2Window,
  isSubmitted,
  canSubmit,
  canAdjust,
  isEditable,

  refreshAll,
  setCell,
  submitWeek,
  enableAdjust,
  disableAdjust,
} = useForecasts({
  venueIdRef: venueId,
  isAdminRef,
  userRef,
});

// ==========================
// Refresh when venue changes
// ==========================
let refreshToken = 0;

async function refreshForecastsForVenue() {
  const currentToken = ++refreshToken;

  if (!venueId.value) return;

  await refreshAll();

  if (currentToken !== refreshToken) return;
}

watch(
  venueId,
  async (newVenueId, oldVenueId) => {
    if (!newVenueId) return;
    if (newVenueId === oldVenueId) return;

    await refreshForecastsForVenue();
  },
  { immediate: true }
);

// ==========================
// Keep ForecastView synced with selected venue
// ==========================
function syncVenueFromStorage() {
  const nextVenueId = getStoredVenueId();

  if (nextVenueId !== venueId.value) {
    venueId.value = nextVenueId;
  }
}

function handleStorageEvent(event) {
  if (event.key === "venue") {
    syncVenueFromStorage();
  }
}

function handleVenueChangedEvent(event) {
  const detail = event?.detail || {};

  const nextVenueId = cleanVenueId(
    detail.venueId ||
      detail.id ||
      detail.venue ||
      detail.value ||
      localStorage.getItem("venue")
  );

  if (nextVenueId && nextVenueId !== venueId.value) {
    localStorage.setItem("venue", nextVenueId);
    venueId.value = nextVenueId;
  } else {
    syncVenueFromStorage();
  }
}

let venuePollTimer = null;

// ==========================
// Auth + role loading
// ==========================
let stopAuthListener = null;

onMounted(() => {
  stopAuthListener = onAuthStateChanged(auth, async (u) => {
    if (u) {
      userRef.value = { uid: u.uid, email: u.email || null };
      await loadUserRole(u.uid);
    } else {
      userRef.value = { uid: null, email: null };
      await loadUserRole(null);
    }
  });

  window.addEventListener("storage", handleStorageEvent);

  window.addEventListener("venue-changed", handleVenueChangedEvent);
  window.addEventListener("coredeck:venue-changed", handleVenueChangedEvent);

  venuePollTimer = window.setInterval(syncVenueFromStorage, 500);

  window.addEventListener("focus", syncVenueFromStorage);
});

onUnmounted(() => {
  if (typeof stopAuthListener === "function") {
    stopAuthListener();
  }

  window.removeEventListener("storage", handleStorageEvent);
  window.removeEventListener("venue-changed", handleVenueChangedEvent);
  window.removeEventListener("coredeck:venue-changed", handleVenueChangedEvent);
  window.removeEventListener("focus", syncVenueFromStorage);

  if (venuePollTimer) {
    window.clearInterval(venuePollTimer);
    venuePollTimer = null;
  }
});

// ==========================
// Lock message helper
// ==========================
function lockMessageForWeek(weekEndingISO) {
  if (!roleLoaded.value) return "Loading permissions…";

  if (!isAdminRef.value && isInCurrentPlus2Window(weekEndingISO)) {
    return "This week is within the current week ending +2 window. Forecast is locked for non-admin users.";
  }

  if (isSubmitted(weekEndingISO) && !isEditable(weekEndingISO)) {
    return "This week has been submitted. Click 'Adjust forecast' to make changes (if allowed).";
  }

  return "";
}
</script>

<style scoped>
.forecast-view {
  margin-top: 18px;
}

.forecast-loading {
  margin: 12px 0 18px;
}

.forecast-progress.progress {
  height: 8px;
  margin: 0;
  border-radius: 999px;
  overflow: hidden;
  background: #c8e6c9;
  box-shadow: none;
}

.forecast-progress .forecast-progress-fill {
  height: 100%;
  border-radius: 999px;
  background: #388e3c;
  animation: forecast-progress-fill 1.35s ease-in-out infinite;
}

@keyframes forecast-progress-fill {
  0% {
    width: 0%;
    opacity: 0.75;
  }

  75% {
    width: 100%;
    opacity: 1;
  }

  100% {
    width: 100%;
    opacity: 0;
  }
}

.forecast-week-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

@media (max-width: 600px) {
  .forecast-view {
    width: 96%;
    margin-top: 12px;
  }

  .forecast-view h4 {
    font-size: 1.8rem;
    margin-top: 0;
  }

  .forecast-loading {
    margin: 10px 0 14px;
  }

  .forecast-week-list {
    gap: 12px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .forecast-view :deep(table) {
    min-width: 680px;
  }

  .forecast-view :deep(.card-content) {
    padding: 14px;
  }
}
</style>
